import "./UserInboxes.scss";

import React, { useEffect, useMemo, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { useNavigate } from "react-router-dom";
import { MdChatBubbleOutline, MdClose } from "react-icons/md";

import SideBar from "../../../components/SideBar/SideBar";
import TopNav from "../../../components/TopNav/TopNav";
import TableColumn from "../../../components/TableColumn/TableColumn";
import api from "../../../services/api";

// Friendly labels + filter chip colors for the discriminator returned by
// GET /admin/user-inboxes. Keep in sync with the server controller at
// zona-superadmin-server/src/controllers/userInboxesAdminController.js
const TYPE_FILTERS = [
  { key: "", label: "All" },
  { key: "private", label: "Private" },
  { key: "group", label: "Group" },
  { key: "bottle_service", label: "Bottle Service" },
  { key: "guestlist", label: "Guestlist" },
  { key: "featured_event", label: "Featured Event" },
];

const TYPE_LABEL = {
  private: "Private",
  group: "Group",
  bottle_service: "Bottle Service",
  guestlist: "Guestlist",
  featured_event: "Featured Event",
};

// Wink/Cheers consent, mirrored from Chat.consent_state on the server.
const GESTURE_LABEL = { wink: "Wink", cheers: "Cheers" };
const CONSENT_LABEL = {
  PENDING: "Pending",
  ACCEPTED: "Accepted",
  DECLINED: "Declined",
};

// Every timestamp in this panel is ET. date-fns `format` rendered in the
// viewer's own zone, so rows read an hour off from the ET clock in the header.
function formatET(d) {
  if (!d) return "\u2014";
  try {
    return (
      new Date(d).toLocaleString("en-US", {
        timeZone: "America/New_York",
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      }) + " ET"
    );
  } catch {
    return "\u2014";
  }
}

const STATUS_LABEL = {
  active: "Active",
  expired: "Expired",
  inactive: "Inactive",
  pending: "Pending",
  confirmed: "Confirmed",
  counter_offered: "Countered",
  paid: "Paid",
  rejected: "Rejected",
};

// Same public bucket the server and the reminder email build photo links from.
const PHOTO_BUCKET = process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || "zona-53cac.appspot.com";
function photoUrl(photo) {
  if (!photo) return null;
  return /^https?:\/\//i.test(photo) ? photo : `https://storage.googleapis.com/${PHOTO_BUCKET}/${photo}`;
}

// Deleted accounts open on their own page; /users/:id shows nothing for them.
function profilePath(p) {
  return p.deleted ? `/deleted-users/${p._id}` : `/users/${p._id}`;
}

function Avatar({ person, size = 24 }) {
  const [failed, setFailed] = useState(false);
  const url = photoUrl(person?.photo);
  const initial = (person?.firstName || person?.email || "?").trim().charAt(0).toUpperCase();
  return url && !failed ? (
    <img className="user-inboxes__avatar" src={url} alt="" width={size} height={size} onError={() => setFailed(true)} />
  ) : (
    <span className="user-inboxes__avatar is-empty" style={{ width: size, height: size }}>{initial}</span>
  );
}

// Table cell: up to 3 people as photo + name, each opening that person's profile.
function ParticipantsCell({ participants, onOpen }) {
  if (!Array.isArray(participants) || participants.length === 0) return "\u2014";
  const shown = participants.slice(0, 3);
  const extra = participants.length - shown.length;
  return (
    <div className="user-inboxes__people">
      {shown.map((p) => (
        <button key={p._id} className="user-inboxes__person" onClick={() => onOpen(p)} title="Open user profile">
          <Avatar person={p} />
          <span>{formatParticipant(p)}</span>
        </button>
      ))}
      {extra > 0 && <span className="user-inboxes__people-more">+{extra} more</span>}
    </div>
  );
}

function UserInboxes() {
  const records = 25;
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  const [typeFilter, setTypeFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");

  // Thread drawer state. viewingChatId !== null → modal open + fetching
  // /admin/user-inboxes/:chatId/messages. thread holds the full response
  // ({ chat, messages, total, truncated }) once loaded.
  const [viewingChatId, setViewingChatId] = useState(null);
  // The table row's participants carry `deleted`; the thread response doesn't.
  const [viewingPeople, setViewingPeople] = useState([]);
  const [thread, setThread] = useState(null);
  const [threadLoading, setThreadLoading] = useState(false);

  // Debounce search so the admin can type without firing a request per
  // keystroke. 350ms feels snappy without thrashing the server.
  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(searchTerm.trim()), 350);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const columns = useMemo(
    () => ["Type", "Participants", "Gesture", "Last Message", "Status", "Updated"],
    [],
  );

  const fetchInboxes = async (page) => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/user-inboxes", {
        params: {
          page,
          records,
          ...(typeFilter ? { type: typeFilter } : {}),
          ...(searchDebounced ? { q: searchDebounced } : {}),
        },
      });

      const list = data?.data || [];
      const formatted = list.map((row) => ({
        _id: row._id,
        people: row.participants || [],
        Type: TYPE_LABEL[row.type] || row.type || "—",
        // Cap at 3 + "+N more" so a 50-person guestlist chat doesn't blow out the column.
        Participants: (
          <ParticipantsCell participants={row.participants} onOpen={(p) => navigate(profilePath(p))} />
        ),
        Gesture: renderGesture(row.gesture),
        "Last Message": renderLastMessage(row.last_message),
        // A pending/declined wink isn't an "Active" chat, so consent wins
        // over the room status for gesture chats.
        Status: consentStatus(row.gesture) || STATUS_LABEL[row.status] || row.status || "—",
        Updated: formatET(row.updatedAt),
      }));

      setRows(formatted);
      // Server returns `total` = the full count of matching chat rows
      // (already narrowed by the active type filter). TableColumn expects
      // the raw record count in its `totalPages` prop and divides by
      // `records` itself to derive the page count — so pass the count
      // through as-is. (Dividing here too double-counted and capped the
      // view at 3 pages.)
      const reportedTotal = Number(data?.total) || formatted.length;
      setTotalPages(reportedTotal);
    } catch (err) {
      console.error("UserInboxes fetch failed:", err);
      setRows([]);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInboxes(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, typeFilter, searchDebounced]);

  // Fetch the message thread whenever a chat is selected for viewing.
  // Resets when modal closes so the next open shows the spinner.
  useEffect(() => {
    if (!viewingChatId) {
      setThread(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setThreadLoading(true);
      try {
        const { data } = await api.get(
          `/admin/user-inboxes/${viewingChatId}/messages`,
        );
        if (!cancelled) setThread(data);
      } catch (err) {
        console.error("Thread fetch failed:", err);
        if (!cancelled) setThread({ chat: null, messages: [], total: 0 });
      } finally {
        if (!cancelled) setThreadLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [viewingChatId]);

  const actions = useMemo(
    () => [
      {
        label: <MdChatBubbleOutline size={22} color="#4F30EA" />,
        onClick: (rowData) => {
          setViewingPeople(rowData.people || []);
          setViewingChatId(rowData._id || rowData.id);
        },
      },
    ],
    [],
  );

  const handleTypeChange = (key) => {
    setTypeFilter(key);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => setCurrentPage(page);

  return (
    <main>
      <TopNav />
      <div className="main-container">
        <SideBar />
        <article className="page-content">
          <section className="user-inboxes">
            <div className="user-inboxes__filter-row">
              {TYPE_FILTERS.map((f) => (
                <button
                  key={f.key || "all"}
                  className={`user-inboxes__chip${typeFilter === f.key ? " is-active" : ""}`}
                  onClick={() => handleTypeChange(f.key)}>
                  {f.label}
                </button>
              ))}
            </div>

            <TableColumn
              title="User Inboxes"
              columns={columns}
              data={rows}
              loading={loading}
              hasSearch={true}
              totalPages={totalPages}
              currentPage={currentPage}
              onPageChange={handlePageChange}
              records={records}
              actions={actions}
              inputSearchProps={{
                value: searchTerm,
                onChange: (e) => setSearchTerm(e.target.value),
                placeholder: "Search by first name, last name, or email",
              }}
            />
          </section>
        </article>
      </div>

      <ThreadModal
        chatId={viewingChatId}
        thread={thread}
        loading={threadLoading}
        deletedIds={new Set(viewingPeople.filter((p) => p.deleted).map((p) => String(p._id)))}
        onClose={() => setViewingChatId(null)}
      />
    </main>
  );
}

// One entry per person: a chat that listed someone twice gave them two receipts, and only the
// first ever flips to read, so read wins and the earliest time is kept.
function dedupeReceipts(receipts) {
  const byUser = new Map();
  for (const r of receipts || []) {
    const prev = byUser.get(r.user);
    if (!prev) { byUser.set(r.user, r); continue; }
    const read = prev.read || r.read;
    const times = [prev.read_at, r.read_at].filter(Boolean).sort();
    byUser.set(r.user, { ...prev, read, read_at: times[0] || null, deleted: prev.deleted || r.deleted });
  }
  return [...byUser.values()];
}

// Who has read one message. Small groups list everyone; big ones fold behind a count.
function Receipts({ receipts, people }) {
  const [open, setOpen] = useState(false);
  const list = dedupeReceipts(receipts);
  if (!list.length) return null;
  // Deleting a message doesn't unread it, so the read state always shows.
  const status = (r) =>
    (r.read ? `read ${r.read_at ? formatET(r.read_at) : "(time not recorded)"}` : "unread") +
    (r.deleted ? " \u00b7 deleted" : "");
  const rows = () =>
    list.map((r) => (
      <span key={r.user} className={`user-inboxes__receipt ${r.read ? "is-read" : "is-unread"}`}>
        {formatParticipant({ _id: r.user, ...people?.[r.user] })}: {status(r)}
      </span>
    ));
  if (list.length <= 4) return <div className="user-inboxes__receipts">{rows()}</div>;
  return (
    <details className="user-inboxes__receipts-group" onToggle={(e) => setOpen(e.currentTarget.open)}>
      <summary>Read by {list.filter((r) => r.read).length} of {list.length}</summary>
      {open && <div className="user-inboxes__receipts">{rows()}</div>}
    </details>
  );
}

function ThreadModal({ chatId, thread, loading, deletedIds, onClose }) {
  const navigate = useNavigate();

  // Closes the modal first so the route-change animation isn't fighting
  // the Modal teardown, then routes to the user-edit page (the same
  // surface the Users tab links to).
  const goToUser = (userId) => {
    if (!userId) return;
    onClose();
    // Read-only profile view, not the edit form: inbox is a browse surface.
    navigate(profilePath({ _id: userId, deleted: deletedIds?.has(String(userId)) }));
  };
  // Message senders carry no photo, so borrow it from the participant list.
  const byId = new Map((thread?.chat?.participants || []).map((p) => [String(p._id), p]));

  const show = !!chatId;
  const messages = thread?.messages || [];
  const chat = thread?.chat;
  const participants = chat?.participants || [];
  const title = chat?.name
    ? chat.name
    : participants.length === 1
    ? formatParticipant(participants[0])
    : participants.length === 2
    ? `${formatParticipant(participants[0])} · ${formatParticipant(participants[1])}`
    : participants.length > 2
    ? `Group · ${participants.length} people`
    : "Chat";

  return (
    <Modal show={show} onHide={onClose} centered size="lg" scrollable>
      <div className="user-inboxes__thread">
        <div className="user-inboxes__thread-header">
          <div>
            <div className="user-inboxes__thread-title">{title}</div>
            {chat?.chatType && (
              <div className="user-inboxes__thread-sub">
                {chat.chatType} · {messages.length} message
                {messages.length === 1 ? "" : "s"}
                {thread?.truncated ? " (truncated at 500)" : ""}
              </div>
            )}
            {messages.some((m) => m.receipts?.length) && (
              <div className="user-inboxes__thread-note">
                Read = opened the chat after the message arrived.
              </div>
            )}
          </div>
          <button className="user-inboxes__thread-close" onClick={onClose}>
            <MdClose size={22} />
          </button>
        </div>

        {participants.length > 0 && (
          <div className="user-inboxes__thread-participants">
            <div className="user-inboxes__thread-participants-label">
              Participants ({participants.length})
            </div>
            <div className="user-inboxes__thread-participants-list">
              {participants.map((p) => (
                <button
                  key={p._id}
                  className="user-inboxes__participant-chip is-clickable"
                  onClick={() => goToUser(p._id)}
                  title="Open user profile">
                  <Avatar person={p} size={20} />
                  {formatParticipant(p)}
                </button>
              ))}
            </div>
          </div>
        )}

        <GestureBanner
          gesture={chat?.gesture}
          participants={participants}
          messageCount={messages.length}
        />

        <div className="user-inboxes__thread-body">
          {loading ? (
            <div className="user-inboxes__thread-loading">Loading messages…</div>
          ) : messages.length === 0 ? (
            <div className="user-inboxes__thread-empty">No messages in this chat yet.</div>
          ) : (
            messages.map((m) => (
              <div key={m._id} className="user-inboxes__msg">
                <div className="user-inboxes__msg-sender">
                  <button
                    className="user-inboxes__msg-sender-link"
                    onClick={() => goToUser(m.sender?._id)}
                    title="Open user profile"
                    disabled={!m.sender?._id}>
                    <Avatar person={{ ...m.sender, photo: byId.get(String(m.sender?._id))?.photo || m.sender?.photo }} size={20} />
                    {[m.sender?.firstName, m.sender?.lastName]
                      .filter(Boolean)
                      .join(" ") || "Unknown user"}
                  </button>
                  <span className="user-inboxes__msg-time">
                    {m.createdAt ? formatET(m.createdAt) : ""}
                  </span>
                </div>
                <div className="user-inboxes__msg-body">
                  {renderMessageBody(m)}
                </div>
                <Receipts receipts={m.receipts} people={thread?.people} />
              </div>
            ))
          )}
        </div>
      </div>
    </Modal>
  );
}

// Explains a thread that is empty on purpose: a wink/cheers creates the chat
// row before either person can type, so a pending one has no messages by design.
function GestureBanner({ gesture, participants, messageCount }) {
  if (!gesture) return null;

  const label = (GESTURE_LABEL[gesture.gesture_type] || "gesture").toLowerCase();
  const senderId = gesture.sender ? String(gesture.sender._id) : null;
  const senderName = gesture.sender ? formatParticipant(gesture.sender) : "Someone";
  const other = (participants || []).find((p) => String(p._id) !== senderId);
  const otherName = other ? formatParticipant(other) : "the other person";
  const sentAt = formatET(gesture.sent_at);

  let line;
  if (gesture.consent_state === "PENDING") {
    line = `${senderName} sent a ${label} on ${sentAt}. ${otherName} hasn't accepted it, so neither of them can send a message here yet.`;
  } else if (gesture.consent_state === "DECLINED") {
    line = `${senderName} sent a ${label} on ${sentAt}. ${otherName} declined it, so this chat is closed.`;
  } else if (messageCount === 0) {
    line = `${senderName} sent a ${label} on ${sentAt}. It was accepted, but neither of them has sent a message yet.`;
  } else {
    line = `Started by a ${label} ${senderName} sent on ${sentAt}.`;
  }

  const state = String(gesture.consent_state || "").toLowerCase();
  return (
    <div className={`user-inboxes__gesture-banner is-${state}`}>
      <span className="user-inboxes__gesture-banner-tag">
        {GESTURE_LABEL[gesture.gesture_type] || "Gesture"} ·{" "}
        {CONSENT_LABEL[gesture.consent_state] || gesture.consent_state}
      </span>
      <span>{line}</span>
    </div>
  );
}

// Table cell: "Cheers from Bastien · Pending".
function renderGesture(gesture) {
  if (!gesture || !gesture.gesture_type) return "\u2014";
  const label = GESTURE_LABEL[gesture.gesture_type] || gesture.gesture_type;
  const state = CONSENT_LABEL[gesture.consent_state] || gesture.consent_state;
  const who = gesture.sender?.firstName?.trim();
  return who ? `${label} from ${who} · ${state}` : `${label} · ${state}`;
}

// Only a non-accepted gesture overrides the Status column.
function consentStatus(gesture) {
  if (!gesture || !gesture.consent_state) return null;
  if (gesture.consent_state === "ACCEPTED") return null;
  return CONSENT_LABEL[gesture.consent_state] || gesture.consent_state;
}

function formatParticipant(p) {
  if (!p) return "Unknown";
  const name = [p.firstName, p.lastName].filter(Boolean).join(" ").trim();
  if (name) return name;
  if (p.email) return p.email;
  return p._id ? `User ${String(p._id).slice(-6)}` : "Unknown";
}

// Card data in words: prices are stored in cents, so 100000 is $1,000.
function centsToDollars(cents) {
  if (!Number.isFinite(cents)) return "\u2014";
  const digits = cents % 100 ? 2 : 0;
  return (cents / 100).toLocaleString("en-US", {
    style: "currency", currency: "USD", minimumFractionDigits: digits, maximumFractionDigits: digits,
  });
}
// The expiry codes expireBookingRequests.js writes (plus one set by hand 2026-09-20). Any other
// reason is the venue's own words, e.g. an SMS rejection, and shows as sent.
const CARD_REASONS = new Map([
  ["organizer_no_response", "Venue didn't answer in time"],
  ["counter_offer_unanswered", "Guest didn't answer the counter offer"],
  ["payment_not_received", "Guest didn't pay in time"],
  ["guest_no_response", "Guest stopped replying"],
]);
// client_secret lets a browser confirm a Stripe payment, so it never shows here.
const CARD_HIDDEN = new Set(["client_secret"]);
const CARD_LABELS = {
  event_name: "Event",
  tier_name: "Table",
  guest_name: "Guest",
  price_cents: "Price",
  amount_cents: "Amount",
  requires_id_verification: "ID check required",
  reason: "Reason",
  request_id: "Request ID",
  payment_intent_id: "Stripe payment",
  counter_offer: "Counter offer",
  tier: "Table ID",
  note: "Note",
};
const isDateKey = (k) => k === "deadline" || k.endsWith("_at");

function labelFor(key, cardType) {
  if (key === "deadline") return cardType === "table_request" ? "Venue answers by" : "Pay by";
  const label = CARD_LABELS[key] || key.replace(/_cents$/, "").replace(/_/g, " ");
  return label.charAt(0).toUpperCase() + label.slice(1);
}

function cardValue(key, value, cardType) {
  if (value === null || value === undefined || value === "") return "\u2014";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (key.endsWith("_cents") && typeof value === "number") return centsToDollars(value);
  if (key === "reason") return CARD_REASONS.get(value) || String(value);
  if (isDateKey(key) && !Number.isNaN(Date.parse(value))) return formatET(value);
  if (Array.isArray(value)) return value.map((v) => cardValue("", v, cardType)).join(", ");
  if (typeof value === "object") {
    return Object.entries(value)
      .filter(([k]) => !CARD_HIDDEN.has(k))
      .map(([k, v]) => `${labelFor(k, cardType)}: ${cardValue(k, v, cardType)}`)
      .join(" \u00b7 ");
  }
  return String(value);
}

function CardDetails({ cardType, data }) {
  if (!data || typeof data !== "object" || Array.isArray(data)) return null;
  const rows = Object.entries(data).filter(([k]) => !CARD_HIDDEN.has(k));
  if (!rows.length) return null;
  return (
    <dl className="user-inboxes__msg-card-data">
      {rows.map(([k, v]) => (
        <div key={k} className="user-inboxes__msg-card-row">
          <dt>{labelFor(k, cardType)}</dt>
          <dd>{cardValue(k, v, cardType)}</dd>
        </div>
      ))}
    </dl>
  );
}

function renderMessageBody(m) {
  if (m.messageType === "card") {
    return (
      <div className="user-inboxes__msg-card">
        <div className="user-inboxes__msg-card-type">{(m.cardType || "card").replace(/_/g, " ")}</div>
        {m.content && <div>{m.content}</div>}
        <CardDetails cardType={m.cardType} data={m.cardData} />
      </div>
    );
  }
  if (m.messageType === "file") {
    return (
      <div className="user-inboxes__msg-files">
        {(m.fileUrls || []).map((url, i) => (
          <a key={i} href={url} target="_blank" rel="noreferrer">
            File {i + 1}
          </a>
        ))}
      </div>
    );
  }
  return <span>{m.content || "(empty)"}</span>;
}

function renderLastMessage(msg) {
  if (!msg) return "—";
  if (msg.messageType === "card") return "[Card]";
  if (msg.messageType === "file") return "[File]";
  const text = (msg.content || "").trim();
  if (!text) return "—";
  return text.length > 60 ? `${text.slice(0, 60)}…` : text;
}

export default UserInboxes;
